import { useState } from 'react';
import { useT } from '../i18n';
import './Assurance.css';

/**
 * The assurance block, rendered as a FAQ accordion that copies the FAQ section
 * on prqpartners.com (`.faq-section` / `.faq-item` / `.plus-icon.faq` in
 * prq-partners.webflow.css): serif section title, hairline-separated rows, a
 * grotesk question with a large Arial "+" that rotates into an accent "×".
 *
 * Items toggle independently, as Webflow's per-item click interaction does,
 * and all start closed — the resting state of the FAQ on the site.
 */
export function Assurance() {
  const t = useT();
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());

  const toggle = (index: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <section className="faq" aria-labelledby="faq-heading">
      <h2 className="faq__title" id="faq-heading">
        {t.assurance.heading}
      </h2>

      <div className="faq__list">
        {t.assurance.items.map((item, index) => {
          const isOpen = open.has(index);
          const last = index === t.assurance.items.length - 1;
          return (
            <div className={last ? 'faq__item faq__item--last' : 'faq__item'} key={item.heading}>
              {/* The whole row is the control, as on the site, where the full
                  faq-head takes the hover and the click. */}
              <h3 className="faq__head">
                <button
                  type="button"
                  className="faq__toggle"
                  id={`faq-toggle-${index}`}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${index}`}
                  onClick={() => toggle(index)}
                >
                  <span className="faq__question">{item.heading}</span>
                  {/* A literal "+" in Arial, per the site. Open rotates it 45°
                      into the "×" and colours it with the accent. */}
                  <span className="faq__icon" aria-hidden="true">
                    +
                  </span>
                </button>
              </h3>

              <div
                className={isOpen ? 'faq__panel faq__panel--open' : 'faq__panel'}
                id={`faq-panel-${index}`}
                role="region"
                aria-labelledby={`faq-toggle-${index}`}
              >
                <div className="faq__clip">
                  <p className="faq__answer">{item.body}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
